import React, {FC, useMemo, useState} from 'react'
import {ModalWrapper} from '../../../../../components/Modals/General/ModalWrapper/ModalWrapper'
import styles from '../serviceRequests.module.css'
import {i18n} from '../../../../../translate/i18n'

type Icon = {
  id: number
  svg?: string
  description?: string
  label?: string
  name?: string
  class?: string
  value?: string
  slug?: string
}

interface IconPickerModalProps {
  isOpen: boolean
  icons: Icon[]
  selectedIconId: number | null
  selectedIcon: string
  setSelectedIcon: (icon: string) => void
  setSelectedIconId: (id: number | null) => void
  onClose: () => void
  onConfirm: () => void
  wrapperModalOpen?: boolean
}

// Normaliza SVG vindo da API (remove width/height/fill/stroke inline)
const normalizeSvg = (svg: string) =>
  svg?.replace(/(width|height)="[^"]*"/gi, '')?.replace(/\s*(fill|stroke)="[^"]*"/gi, '') ?? ''

const iconClassOf = (it: Icon): string =>
  it?.class ?? it?.value ?? it?.name ?? it?.slug ?? it?.id?.toString() ?? ''

const IconPickerModal: FC<IconPickerModalProps> = ({
  isOpen,
  icons,
  selectedIconId,
  selectedIcon,
  setSelectedIcon,
  setSelectedIconId,
  onClose,
  onConfirm,
  wrapperModalOpen = false,
}) => {
  const [search, setSearch] = useState('')

  const filteredIcons = useMemo(() => {
    const term = search.trim().toLowerCase()
    if (!term) return icons

    return icons.filter((it: Icon) => {
      const text = `${it.description ?? it.label ?? it.name ?? it.slug ?? ''}`.toLowerCase()
      return text.includes(term)
    })
  }, [icons, search])

  if (!isOpen && !wrapperModalOpen) return null

  return (
    <ModalWrapper
      height={40}
      confirmAction={onConfirm}
      textAction={i18n.t('service_requests.select_icon_title')}
      confirmButtomColor='primary'
      shouldCloseModal
      isFooterFixed
      onCloseAction={onClose}
    >
      {icons.length === 0 ? (
        <div className='text-center text-muted py-5'>
          {i18n.t('service_requests.no_icons_available')}
        </div>
      ) : (
        <>
          <div className={styles.iconSearchContainer ?? ''} style={{padding: '0.5rem 0'}}>
            <input
              type='text'
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={'Pesquisar por nome ou descrição...'}
              className={styles.iconSearchInput ?? ''}
              style={{width: '100%', padding: '0.5rem', borderRadius: 4, border: '1px solid #ddd'}}
            />
          </div>

          {filteredIcons.length === 0 ? (
            <div className='text-center text-muted py-5'>{'Nenhum ícone encontrado'}</div>
          ) : (
            <div className={styles.iconScroll}>
              <div className={styles.iconGrid}>
                {filteredIcons.map((it: Icon, idx: number) => {
                  const cls = iconClassOf(it)
                  const active = selectedIconId === it.id
                  const hasInlineSvg = !!it?.svg
                  return (
                    <button
                      key={`${it.id ?? idx}`}
                      type='button'
                      className={`${styles.iconCard} ${active ? styles.iconActive : ''}`}
                      onClick={() => {
                        setSelectedIconId(it.id) // seleciona pelo ID
                        setSelectedIcon(cls) // salva classe como fallback (se houver)
                      }}
                      title={cls || `${i18n.t('service_requests.form_icon')} ${idx}`}
                    >
                      <span className={styles.iconGlyph}>
                        {hasInlineSvg ? (
                          <span dangerouslySetInnerHTML={{__html: normalizeSvg(it.svg!)}} />
                        ) : (
                          <i className={`bi ${cls || 'bi-image'}`} />
                        )}
                      </span>
                      <span className={styles.iconName}>
                        {it?.label ??
                          it?.name ??
                          cls ??
                          `${i18n.t('service_requests.form_icon')} ${idx}`}
                      </span>
                    </button>
                  )
                })}
              </div>
            </div>
          )}
        </>
      )}
    </ModalWrapper>
  )
}

export {IconPickerModal}
